import type { EChartsOption } from 'echarts';
import { color as echartsColor, graphic } from 'echarts/core';

import type { Granularity, MeterType, SeriesData } from '../../../../types';
import { getUsageTimeAxisLabelFormat, getUsageValueAxisOptions, toUsageChartData } from '../usageChartPrimitives';

import { getMarkLines } from './getMarkLine';
import { getTooltipFormatter, type UsageTooltipComponent } from './getTooltipFormatter';

// Matches plan usage projection prototype (info purple + neutral grey)
const ACTUAL_PURPLE = 'rgba(137, 96, 194, 1)';
const actualGradient = new graphic.LinearGradient(0, 0, 0, 1, [
  { offset: 0, color: 'rgba(137, 96, 194, 0.7)' },
  { offset: 1, color: 'rgba(137, 96, 194, 0)' },
]);

const PROJECTED_GREY = 'rgba(180, 180, 180, 1)';
const projectedGradient = new graphic.LinearGradient(0, 0, 0, 1, [
  { offset: 0, color: 'rgba(180, 180, 180, 0.2)' },
  { offset: 1, color: 'rgba(180, 180, 180, 0)' },
]);

const barWidthRatio = 0.8;

function getAreaGradient(color: string) {
  return new graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: echartsColor.modifyAlpha(color, 0.7) },
    { offset: 1, color: echartsColor.modifyAlpha(color, 0) },
  ]);
}

type UsageChartOptions = {
  series: SeriesData;
  chartType: 'line' | 'bar';
  abbreviatedName: string;
  precision: number;
  usageResets: number[];
  granularity: Granularity;
  showSymbol: boolean;
  limitValue?: number;
  yAxisMax?: number;
  avgUsage?: number;
  condenseLabels?: boolean;
  hasAreaStyles?: boolean;
  meterType?: MeterType;
  trialEndUTC?: number;
  yAxisName?: string;
  seriesName?: string;
  tooltipComponents?: readonly UsageTooltipComponent[];
  seriesColor?: string;
  // Cumulative projected usage as time-stamped points for the full billing period, matching the
  // shape of `series`. The dashed projected line joins the actual line where the actuals end.
  projectedSeries?: SeriesData;
};

function getBarBoundaryGap(series: SeriesData): [number, number] | undefined {
  const timestamps = series.map(({ time }) => time).sort((left, right) => left - right);
  const firstTimestamp = timestamps[0];
  const lastTimestamp = timestamps.at(-1);
  if (firstTimestamp === undefined || lastTimestamp === undefined) {
    return undefined;
  }

  const range = lastTimestamp - firstTimestamp;
  let minimumGap = Infinity;
  let previousTimestamp = firstTimestamp;

  for (const timestamp of timestamps.slice(1)) {
    const gap = timestamp - previousTimestamp;
    if (gap > 0) {
      minimumGap = Math.min(minimumGap, gap);
    }
    previousTimestamp = timestamp;
  }

  if (!range || !Number.isFinite(minimumGap)) {
    return undefined;
  }

  const relativeBarWidth = (minimumGap / range) * barWidthRatio;
  const boundaryGap = relativeBarWidth / (2 * (1 - relativeBarWidth));

  return [boundaryGap, boundaryGap];
}

export function getUsageChartOptions({
  series,
  chartType,
  abbreviatedName,
  precision,
  usageResets,
  granularity,
  showSymbol,
  limitValue,
  yAxisMax,
  avgUsage,
  condenseLabels,
  hasAreaStyles,
  trialEndUTC,
  yAxisName,
  seriesName,
  tooltipComponents,
  projectedSeries,
  seriesColor,
}: UsageChartOptions): EChartsOption {
  // Convert SeriesDataItem objects to ECharts data format including context information
  const chartData = toUsageChartData(series);

  // With a projection, the solid line ends at the last actual (non-null) point — the junction,
  // today, since the projection anchors on today's cumulative — and the dashed line continues
  // from there to the month-end total.
  let actualChartData = chartData;
  let projectedChartData: Array<{ value: [number, number] }> | undefined;

  if (projectedSeries && projectedSeries.length > 0 && hasAreaStyles) {
    const lastActualTime = chartData.reduce<number | undefined>(
      (acc, item) => (item.value[1] !== null ? item.value[0] : acc),
      undefined,
    );

    if (lastActualTime !== undefined) {
      // Solid line stops at the junction: null out anything after it (usually none).
      actualChartData = chartData.map((item) =>
        item.value[0] > lastActualTime ? { ...item, value: [item.value[0], null] as [number, number | null] } : item,
      );

      const filtered = projectedSeries
        .filter((point) => point.time >= lastActualTime)
        .map((point) => ({
          value: [point.time, point.value ?? 0] as [number, number],
          ...(point.time === lastActualTime && { symbol: 'none' as const }),
        }));
      // Empty array is truthy — keep undefined so downstream checks work.
      projectedChartData = filtered.length > 0 ? filtered : undefined;
    }
  }

  const xAxisFormat = getUsageTimeAxisLabelFormat(granularity, { condenseLabels });
  const barBoundaryGap = chartType === 'bar' ? getBarBoundaryGap(series) : undefined;

  return {
    useUTC: true,
    animation: false,
    grid: {
      left: condenseLabels ? 8 : 16,
      right: condenseLabels ? 8 : 16,
      top: yAxisName ? 40 : 16,
      bottom: 8,
      containLabel: true,
    },
    xAxis: {
      type: 'time',
      splitNumber: 3,
      ...(chartType === 'bar' && {
        containShape: false,
        ...(barBoundaryGap && {
          boundaryGap: barBoundaryGap,
        }),
      }),
      axisLabel: {
        formatter: xAxisFormat,
      },
    },
    yAxis: {
      ...getUsageValueAxisOptions({ max: yAxisMax }),
      ...(yAxisName && { name: yAxisName, nameGap: 8 }),
    },
    series: [
      {
        type: chartType,
        ...(seriesName && { name: seriesName }),
        ...(seriesColor && {
          color: seriesColor,
          itemStyle: { color: seriesColor },
          lineStyle: { color: seriesColor, width: 2 },
        }),
        ...(projectedChartData && {
          name: 'Actual',
          color: ACTUAL_PURPLE,
          itemStyle: { color: ACTUAL_PURPLE },
          lineStyle: { color: ACTUAL_PURPLE, width: 2 },
        }),
        data: actualChartData as Array<{ value: [number, number]; primaryContext?: string | undefined }>, // Echarts allows null values, but the type definition doesn't, also supports custom properties
        smooth: true,
        ...(hasAreaStyles && { areaStyle: { color: seriesColor ? getAreaGradient(seriesColor) : actualGradient } }),
        showSymbol,
        markLine: getMarkLines({
          abbreviatedName,
          limitValue,
          avgUsage,
          usageResets,
          precision,
          condenseLabels,
          trialEndUTC,
        }),
        connectNulls: false,
        emphasis: {
          focus: 'none',
          // A single purple dot follows the cursor along the actual line, including the junction.
          ...(projectedChartData && { itemStyle: { color: ACTUAL_PURPLE } }),
        },
      },
      ...(projectedChartData
        ? [
            {
              type: 'line' as const,
              name: 'Projected',
              color: PROJECTED_GREY,
              itemStyle: { color: PROJECTED_GREY },
              data: projectedChartData,
              smooth: true,
              showSymbol: false,
              lineStyle: { type: 'dashed' as const, color: PROJECTED_GREY, width: 2 },
              areaStyle: { color: projectedGradient },
              connectNulls: false,
              // Disable emphasis so the actual (purple) series owns the shared junction's hover
              // symbol; the junction stays a single purple dot instead of flickering to grey.
              emphasis: { disabled: true as const },
            },
          ]
        : []),
    ],
    tooltip: {
      trigger: 'axis',
      formatter: getTooltipFormatter(abbreviatedName, precision, granularity, condenseLabels, tooltipComponents),
    },
  };
}
